<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import Preview from "../../../base-components/Preview";
import { userUserStore } from "../../../stores/user";
import { Pedido, pedidoConverter } from "@fixeduy/restaurantestypes";
// import PedidosListadoTotales from "./PedidosListadoTotales.vue";
import { useCollection } from "vuefire";
import {
  type QueryConstraint,
  collection,
  getFirestore,
  orderBy,
  query,
  where
} from "firebase/firestore";
import PedidosLiveListadoUnico from "./PedidosLiveListadoUnico.vue";
import Button from "../../../base-components/Button/Button.vue";
import FormSelect from "../../../base-components/Form/FormSelect.vue";
import { ESTADOS_PEDIDO } from "../../../constants/estadosPedido";
import { useSectoresStore } from "../../../services/sectores";
import { useMesasStore } from "../../../services/mesas";

const userStore = userUserStore();
const sectoresStore = useSectoresStore();
const mesasStore = useMesasStore();

// Solo los estados que se usan como filtro en esta vista (mismo orden que antes)
const estados = ESTADOS_PEDIDO.filter(e =>
  ["abierto", "borrador", "cerrado", "cancelado"].includes(
    e.value
  )
);

const sectores = computed(() =>
  [
    { value: "", label: "Todos" },
    ...(sectoresStore.sectores ?? []).map(s => ({
      value: s.id,
      label: s.nombre
    }))
  ]
);

const estadosSeleccionados = ref<string[]>(["abierto"]);
const sector = ref<string>("");

const toggleEstado = (value: string) => {
  const idx = estadosSeleccionados.value.indexOf(value);
  if (idx >= 0) {
    const next = estadosSeleccionados.value.filter((_, i) => i !== idx);
    estadosSeleccionados.value = next.length ? next : [value];
  } else {
    estadosSeleccionados.value = [...estadosSeleccionados.value, value];
  }
};

// Cargar datos base para poder filtrar por sector de la mesa.
onMounted(async () => {
  if (!sectoresStore.sectores.length && !sectoresStore.cargando) {
    await sectoresStore.getFromFirestore();
  }
  if (!mesasStore.mesas.length && !mesasStore.cargando) {
    await mesasStore.getFromFirestore();
  }
});

// Inicializar sector con el primero disponible cuando carguen los sectores.
watch(
  () => sectores.value,
  sectoresOptions => {
    const existeSeleccion = sectoresOptions.some(s => s.value === sector.value);
    if (!existeSeleccion) {
      sector.value = "";
    }
  },
  { immediate: true }
);

const db = getFirestore();

const pedidosQuery = computed(() => {
  const empresaId = userStore.empresa?.id;
  if (!empresaId) return null; // evita romper si todavía no cargó empresa

  const constraints: QueryConstraint[] = [];
  if (estadosSeleccionados.value.length > 0) {
    constraints.push(where("estado", "in", estadosSeleccionados.value));
  }
  constraints.push(orderBy("fechaCreacion", "desc"));

  return query(
    collection(db, `empresas/${empresaId}/pedidos`).withConverter(
      pedidoConverter
    ),
    ...constraints
  );
});

const pedidos = useCollection(pedidosQuery);

const sectorPorMesaId = computed(() => {
  const mapa = new Map<string, string | undefined>();
  for (const mesa of mesasStore.mesas ?? []) {
    mapa.set(mesa.id, mesa.sectorId ?? undefined);
  }
  return mapa;
});

const pedidosFiltrados = computed(() => {
  const lista = pedidos.value ?? [];
  if (!sector.value) return lista;

  return lista.filter((pedido: Pedido) => {
    if (!pedido.mesaId) return false;
    return sectorPorMesaId.value.get(pedido.mesaId) === sector.value;
  });
});
</script>

<template>
  <Preview class="box mt-4">
    <div class="px-4 pt-3 pb-2">
      <div class="flex flex-wrap items-center gap-3">
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="e in estados"
            :key="e.value"
            type="button"
            class="px-3 py-2 rounded-xl border text-sm font-medium transition"
            :class="
              estadosSeleccionados.includes(e.value)
                ? `${e.badgeClasses} shadow-sm`
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 dark:border-darkmode-400 dark:hover:bg-darkmode-800 dark:text-slate-200'
            "
            @click="toggleEstado(e.value)"
          >
            {{ e.label }}
          </Button>
        </div>
        <div v-if="sectores.length" class="min-w-[180px] ml-auto">
          <label
            class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"
          >
            Sector
          </label>
          <FormSelect
            v-model="sector"
            class="rounded-xl shadow-sm focus:ring-2 focus:ring-primary"
          >
            <option v-for="s in sectores" :key="s.value" :value="s.value">
              {{ s.label }}
            </option>
          </FormSelect>
        </div>
      </div>
    </div>

    <div class="col-span-12 px-4 pb-4 pt-1">
      <div v-if="pedidosFiltrados.length === 0" class="text-center py-12 text-slate-500">
        No hay comandas para mostrar.
      </div>
      <div
        v-else
        class="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <PedidosLiveListadoUnico
          v-for="pedido in pedidosFiltrados"
          :key="pedido.id"
          :pedido="pedido"
        />
      </div>
    </div>
  </Preview>
</template>

<style scoped>
.bloqueado {
  color: rgb(185 28 28);
  padding: 0 0 0 10px;
}
</style>
